import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import { NetworkKey } from "@/lib/constants/cryptos";
import { Token } from "@/lib/classes/BaseNetwork";
import CallbackProvider from "@/lib/context/callback-provider";
import NetworkProvider from "@/lib/context/network-provider";
import RatesProvider from "@/lib/context/rates-provider";
import { WidgetContext } from "@/lib/context/widget-context";
import LoginProvider from "@/lib/context/login-provider";
import { mapMarketData } from "@/lib/helpers/token-mapping";
import { StytchHeadlessClient } from "@stytch/vanilla-js/headless";
import { StytchProvider } from "@stytch/react";

export interface CallbackProps {
  onGetAddresses?: (props: Record<string, string>) => void;
  onGetBalance?: (props: { [coin: string]: string }) => void;
  onWidgetOpenChange?: (isOpen: boolean) => void;
  widgetOpen?: boolean;
  isLoading?: boolean;
  hasAccount?: boolean;
}

const stytchOptions = {
  cookieOptions: {
    opaqueTokenCookieName: "stytch_session",
    jwtCookieName: "stytch_session_jwt",
    path: "",
    availableToSubdomains: true,
    domain: "bitcoin.com",
  },
};

export interface MiscProps {
  redirectUrl?: string;
  baseUrl: string;
  stytchPublicToken: string;
}

const reducer = (
  state: Record<NetworkKey, Token[]>,
  action: { key: NetworkKey; tokens: Token[] },
) => ({
  ...state,
  [action.key]: action.tokens,
});

export const WidgetProvider: FC<
  PropsWithChildren<CallbackProps & MiscProps>
> = ({
  children,
  onGetAddresses,
  onGetBalance,
  onWidgetOpenChange,
  redirectUrl,
  baseUrl,
  stytchPublicToken,
}) => {
  const [balances, setBalances] = useReducer(reducer, {});
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  const [widgetOpen, setWidgetOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stytch, setStytch] = useState<StytchHeadlessClient>();
  const hasAccount = Object.keys(addresses).length > 0;

  const url = redirectUrl || import.meta.env.VITE_REDIRECT_URL;

  const tokensArray = useMemo(() => {
    const flattenedTokens = Object.entries(balances).reduce((prev, curr) => {
      const [chain, tokens] = curr;

      return [...prev, ...Object.values(tokens).map(t => ({ ...t, chain }))];
    }, [] as Token[]);

    const mappedId = mapMarketData(flattenedTokens);

    return mappedId;
  }, [balances]);

  useEffect(() => {
    setStytch(new StytchHeadlessClient(stytchPublicToken, stytchOptions));
  }, [stytchPublicToken]);

  const value = useMemo(
    () => ({
      addresses,
      balances,
      tokensArray,
      isLoading,
      widgetOpen,
      setAddresses,
      setBalances,
      setWidgetOpen,
      setIsLoading,
      redirectUrl: url,
      baseUrl,
      stytch,
      stytchPublicToken,
    }),
    [
      addresses,
      balances,
      tokensArray,
      isLoading,
      widgetOpen,
      url,
      stytch,
      baseUrl,
      stytchPublicToken,
    ],
  );

  return (
    <WidgetContext.Provider value={value}>
      <CallbackProvider
        onGetAddresses={onGetAddresses}
        onGetBalance={onGetBalance}
        onWidgetOpenChange={onWidgetOpenChange}
        widgetOpen={widgetOpen}
        isLoading={isLoading}
        hasAccount={hasAccount}
      >
        {stytch ? (
          <StytchProvider stytch={stytch}>
            <LoginProvider>
              <NetworkProvider>
                <RatesProvider>{children}</RatesProvider>
              </NetworkProvider>
            </LoginProvider>
          </StytchProvider>
        ) : (
          children
        )}
      </CallbackProvider>
    </WidgetContext.Provider>
  );
};
